import { produce } from 'immer';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useLocation, useParams } from 'react-router-dom';
import {
  getErrorMessage,
  hasValidationErrors,
  setValidationErrors,
  showErrorToast,
  updateQueryStringParameter,
} from '../../../../../../../common/global/helpers';
import useMount from '../../../../../../../common/hooks/useMount';
import Breadcrumbs from '../../../../../../../common/widgets/Breadcrumbs';
import Loader from '../../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../../common/widgets/LoadingSpinner';
import {
  Notification,
  NotificationType,
} from '../../../../../../../common/widgets/Notification';
import { CustomerBudgetContext } from '../../../../../contexts/CustomerBudgetContext';
import { CardType } from '../../../../../enums';
import { ComputedCardProductPriceModel } from '../../../../../models/Cards';
import { Dictionary } from '../../../../../models/Categories';
import { ErrorState, ErrorType } from '../../../../../models/Errors';
import { resources } from '../../../../../resources/resources';
import { cardService } from '../../../../../services/card';
import applicationCrumbs from '../../../../ApplicationCrumbs';
import { LimitErrorDetailsDialog } from '../../../../Common/Dialogs/LimitErrorDetailsDialog';
import PlatformInformation from '../../../../Common/PlatformInformation';
import MyAccountMenu from '../../../Common/MyAccountMenu';
import { TopUpGenericCard } from '../../Common/TopUpGenericCard';
import { GenericCardData, TopUpContextType } from '../../Common/types';
import { SuccessDialog } from '../SuccessDialog';

interface TopUpGenericCardState {
  cardType: CardType;
  genericCardData: GenericCardData;
  mainImage?: string;
  isSuccessDialogVisible?: boolean;
}

export const TopUpGenericCardPage = () => {
  const { customerBudget, setCustomerBudget } = useContext(
    CustomerBudgetContext,
  );

  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const [state, setState] = useState<TopUpGenericCardState | null>(null);
  const isSubmittingRef = useRef(false);
  const [showErrorPanel, setShowErrorPanel] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorState>({});
  const [showLimitDialog, setShowLimitDialog] = useState<boolean>(false);
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const loadCardDetails = async (abortController?: AbortController) => {
    if (!params?.cardTypeId) return;
    const cardTypeId = Number.parseInt(params.cardTypeId);

    const resp = await cardService.getTopUpCardDetailsByCardType(
      cardTypeId,
      TopUpContextType.TopUp,
      {
        signal: abortController?.signal,
      },
    );

    if (abortController?.signal.aborted) return;

    if (!resp.hasErrors) {
      const topUpDetails = resp.data.topUpDetails;

      if (topUpDetails) {
        // only to be displayed for the ops team, not actually used in code
        updateQueryStringParameter(
          'productId',
          topUpDetails.productId.toString(),
        );
      }

      if (resp.data.cardProducts.length === 1) {
        setState({
          cardType: cardTypeId,
          mainImage: resp.data.mainImage,
          genericCardData: {
            cardSeries: location.state?.cardSeries,
            thumbnail: resp.data.thumbnail,
            products: resp.data.cardProducts,
            selectedProduct: resp.data.cardProducts[0],
            topUpDetails: topUpDetails,
          },
        });
      } else {
        setState({
          cardType: cardTypeId,
          genericCardData: {
            cardSeries: location.state?.cardSeries,
            thumbnail: resp.data.thumbnail,
            products: resp.data.cardProducts,
            selectedProduct: resp.data.cardProducts.find(
              e => e.productId === topUpDetails?.productId,
            ),
            topUpDetails: topUpDetails,
          },
          mainImage: resp.data.mainImage,
        });
      }
    } else {
      setShowErrorPanel(true);
    }
  };

  const saveData = async (
    productId: number,
    options: Dictionary<any>,
    serializedOptions: string,
    price: ComputedCardProductPriceModel,
  ) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    if (!state) return null;
    setState(
      produce(draft => {
        draft!.genericCardData.isSaveButtonLoading = true;
      }),
    );
    const resp = await cardService.topUpCard({
      productId,
      options: serializedOptions,
    });
    setState(
      produce(draft => {
        draft!.genericCardData.isSaveButtonLoading = false;
      }),
    );
    if (!resp.hasErrors) {
      setState(
        produce(draft => {
          draft!.isSuccessDialogVisible = true;
        }),
      );
      setCustomerBudget(
        produce(customerBudget, d => {
          d.benefitBudgetBalance -= price.price;
        }),
      );
    } else {
      isSubmittingRef.current = false;
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        showErrorToast(translate(resources.employeeCards.topUp.errorMessage));
      }
    }
  };

  const clearErrors = (optionId: number | string) => {
    setErrors(
      produce(draft => {
        if (draft) {
          draft[optionId] = [];
          draft.null = [];
        }
      }),
    );
  };

  useMount(abortController => {
    loadCardDetails(abortController);
  });

  if (!params?.cardTypeId) return null;

  return (
    <>
      <Breadcrumbs entries={[crumbs.home, crumbs.cards, crumbs.topUpCard]} />
      <section className="section-main section-actions my-account-page top-up-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <div className="panel padding-xl">
              <div className="btn-panel-back-wrapper">
                <a onClick={() => navigate(-1)} className="btn btn-panel-back">
                  <span className="btn-text">{translate(resources.cards)}</span>
                </a>
              </div>
              <h1>{translate(resources.employeeCards.topUp.title)}</h1>
              {hasValidationErrors(errors, 'null') && (
                <Notification
                  type={NotificationType.Error}
                  messages={errors.null.map(e => ({
                    message: getErrorMessage(e, translate),
                    onShowDetailsClick: e.payload?.isAutobudgetLimitError
                      ? () => setShowLimitDialog(true)
                      : undefined,
                  }))}
                />
              )}
              <Loader
                loading={!state}
                loader={<LoadingSpinner />}
                showErrorPanel={showErrorPanel}
              >
                {!!state && (
                  <>
                    <TopUpGenericCard
                      contextType={TopUpContextType.TopUp}
                      cardType={state.cardType}
                      genericCardData={state.genericCardData}
                      setGenericCardData={newData =>
                        setState(
                          produce(d => {
                            d!.genericCardData = newData;
                          }),
                        )
                      }
                      onSaveClick={saveData}
                      errors={errors}
                      setErrors={setErrors}
                      clearErrors={clearErrors}
                    />
                    {state.isSuccessDialogVisible && (
                      <SuccessDialog mainImage={state.mainImage} />
                    )}
                  </>
                )}
              </Loader>
            </div>
          </div>
        </div>
      </section>
      {showLimitDialog && (
        <LimitErrorDetailsDialog
          closeDialog={() => setShowLimitDialog(false)}
        />
      )}
    </>
  );
};
